import React from 'react';
import {
  faFileInvoice,
  faTruck,
  faUserSlash,
  faBoxOpen,
  faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import * as S from './styles';
import Table from '../../../components/interface/Tabela';
import KpiCards from '../../../components/interface/KpiCards';
import EstadoVazio from '../../../components/interface/EstadoVazio';
import Filtros from '../../../components/interface/Filtros';
import { DateInput } from '../../../components/interface/DateInput';
import { dataPtBr } from '../../../utils/data';
import { agrupaPorEntregador, chavesDuplicadas, resumoConferencia } from './conferencia';
import { EntregadorCadastrado, NotaDoDia } from './types';

export interface ConferenciaDiaProps {
  data: string;
  notas: NotaDoDia[];
  entregadores: EntregadorCadastrado[];
  erro: string;
  filtrosAbertos: boolean;
  onToggleFiltros: () => void;
  onTrocarData: (data: string) => void;
}

const ConferenciaDiaView: React.FC<ConferenciaDiaProps> = ({
  data,
  notas,
  entregadores,
  erro,
  filtrosAbertos,
  onToggleFiltros,
  onTrocarData,
}) => {
  const linhas = agrupaPorEntregador(notas, entregadores);
  const duplicadas = chavesDuplicadas(notas);
  const contagem = resumoConferencia(notas, entregadores);

  return (
    <S.Container>
      {erro && <S.Erro role="alert">{erro}</S.Erro>}

      <Filtros
        aberto={filtrosAbertos}
        onToggle={onToggleFiltros}
        badges={[`Data: ${dataPtBr(data)}`]}
      >
        <S.CampoData>
          <DateInput
            initialValue={data}
            setValue={onTrocarData}
            id="data-conferencia"
            hasLabel={true}
            labelPosition="left"
            label="Data"
            required={true}
          />
        </S.CampoData>
      </Filtros>

      {duplicadas.length > 0 && (
        <S.Alerta role="alert">
          <strong>
            {duplicadas.length === 1
              ? '1 chave lançada mais de uma vez neste dia'
              : `${duplicadas.length} chaves lançadas mais de uma vez neste dia`}
          </strong>
          <ul>
            {duplicadas.map((dup) => (
              <li key={dup.chave}>
                {dup.chave} — {dup.quantidade}x ({dup.entregadores.join(', ')})
              </li>
            ))}
          </ul>
        </S.Alerta>
      )}

      {linhas.length > 0 && (
        <KpiCards
          itens={[
            { rotulo: 'Notas', valor: contagem.notas, icone: faFileInvoice },
            { rotulo: 'Com carga', valor: contagem.comCarga, icone: faTruck },
            { rotulo: 'Sem carga', valor: contagem.semCarga, icone: faUserSlash },
            {
              rotulo: 'Sem produtos',
              valor: contagem.semProdutos,
              icone: faBoxOpen,
            },
          ]}
        />
      )}

      {linhas.length === 0 ? (
        <EstadoVazio
          icone={faClipboardCheck}
          titulo={`Nenhuma nota em ${dataPtBr(data)}`}
          descricao="Escolha outra data nos filtros. Se o dia estiver certo, nenhum manifesto foi sincronizado ainda."
        />
      ) : (
        <Table
          data={linhas}
          columns={[
            { key: 'entregador', header: 'Entregador', filterable: true },
            {
              key: 'notas',
              header: 'Notas',
              width: '110px',
              td: (linha: any) =>
                linha.notas > 0 ? (
                  <span>{linha.notas}</span>
                ) : (
                  <S.SemCarga>Sem carga</S.SemCarga>
                ),
            },
            { key: 'manifestos', header: 'Manifestos' },
            { key: 'itens', header: 'Itens', width: '90px' },
            {
              key: 'semProdutos',
              header: 'Notas sem produto',
              width: '150px',
              td: (linha: any) =>
                linha.semProdutos > 0 ? (
                  <S.Pendencia>{linha.semProdutos}</S.Pendencia>
                ) : (
                  <span>0</span>
                ),
            },
          ]}
          exportable={true}
        />
      )}
    </S.Container>
  );
};

export default ConferenciaDiaView;
